(() => {
  const announcementSelector = '[data-drdent-announcement]';
  const instances = new WeakMap();
  const headerSelector = '.drdent-header';
  const headerInstances = new WeakMap();
  const videoSelector = '[data-drdent-video]';
  const videoInstances = new WeakMap();
  const videoActivations = new WeakMap();
  let videoObserver;
  const faqSelector = '[data-drdent-faq]';
  const faqInstances = new WeakMap();

  const announcementsWithin = (root) => {
    const announcements = [];
    if (root?.matches?.(announcementSelector)) announcements.push(root);
    root?.querySelectorAll?.(announcementSelector).forEach((announcement) => announcements.push(announcement));
    return announcements;
  };

  const initializeAnnouncement = (announcement) => {
    if (instances.has(announcement) || announcement.dataset.drdentAnnouncementInitialized === 'true') return;

    const slides = [...announcement.querySelectorAll('[data-announcement-slide]')];
    const controls = [...announcement.querySelectorAll('[data-direction]')];
    let activeSlide = slides.findIndex((slide) => !slide.hidden);
    let intervalId;
    let pausedByFocus = false;
    let pausedByHover = false;
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    if (slides.length < 2 || activeSlide < 0) return;

    const showSlide = (nextIndex) => {
      activeSlide = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        slide.hidden = index !== activeSlide;
      });
    };

    const stopRotation = () => {
      window.clearInterval(intervalId);
      intervalId = undefined;
    };

    const startRotation = () => {
      stopRotation();
      if (reducedMotion || pausedByFocus || pausedByHover) return;
      intervalId = window.setInterval(() => showSlide(activeSlide + 1), 5000);
    };

    const controlListeners = controls.map((control) => {
      const listener = () => {
        showSlide(activeSlide + (control.dataset.direction === 'previous' ? -1 : 1));
        startRotation();
      };
      control.addEventListener('click', listener);
      return { control, listener };
    });
    const onMouseEnter = () => {
      pausedByHover = true;
      stopRotation();
    };
    const onMouseLeave = () => {
      pausedByHover = false;
      startRotation();
    };
    const onFocusIn = () => {
      pausedByFocus = true;
      stopRotation();
    };
    const onFocusOut = (event) => {
      if (!announcement.contains(event.relatedTarget)) {
        pausedByFocus = false;
        startRotation();
      }
    };

    announcement.addEventListener('mouseenter', onMouseEnter);
    announcement.addEventListener('mouseleave', onMouseLeave);
    announcement.addEventListener('focusin', onFocusIn);
    announcement.addEventListener('focusout', onFocusOut);
    announcement.dataset.drdentAnnouncementInitialized = 'true';
    instances.set(announcement, () => {
      stopRotation();
      controlListeners.forEach(({ control, listener }) => control.removeEventListener('click', listener));
      announcement.removeEventListener('mouseenter', onMouseEnter);
      announcement.removeEventListener('mouseleave', onMouseLeave);
      announcement.removeEventListener('focusin', onFocusIn);
      announcement.removeEventListener('focusout', onFocusOut);
      instances.delete(announcement);
      delete announcement.dataset.drdentAnnouncementInitialized;
    });
    startRotation();
  };

  const initializeAnnouncements = (root) => {
    announcementsWithin(root).forEach(initializeAnnouncement);
  };

  const disposeAnnouncements = (root) => {
    announcementsWithin(root).forEach((announcement) => instances.get(announcement)?.());
  };

  const headersWithin = (root) => {
    const headers = [];
    if (root?.matches?.(headerSelector)) headers.push(root);
    root?.querySelectorAll?.(headerSelector).forEach((header) => headers.push(header));
    return headers;
  };

  const initializeHeader = (header) => {
    if (headerInstances.has(header) || header.dataset.drdentHeaderInitialized === 'true') return;

    let lastScrollY = Math.max(window.scrollY ?? 0, 0);
    let focused = false;
    const onScroll = () => {
      const currentScrollY = Math.max(window.scrollY ?? 0, 0);
      if (focused || currentScrollY <= 8 || currentScrollY < lastScrollY) {
        header.classList.remove('drdent-header--hidden');
      } else if (currentScrollY > lastScrollY) {
        header.classList.add('drdent-header--hidden');
      }
      lastScrollY = currentScrollY;
    };
    const onFocusIn = () => {
      focused = true;
      header.classList.remove('drdent-header--hidden');
    };
    const onFocusOut = (event) => {
      if (!header.contains(event.relatedTarget)) focused = false;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    header.addEventListener('focusin', onFocusIn);
    header.addEventListener('focusout', onFocusOut);
    header.dataset.drdentHeaderInitialized = 'true';
    headerInstances.set(header, () => {
      window.removeEventListener('scroll', onScroll);
      header.removeEventListener('focusin', onFocusIn);
      header.removeEventListener('focusout', onFocusOut);
      header.classList.remove('drdent-header--hidden');
      headerInstances.delete(header);
      delete header.dataset.drdentHeaderInitialized;
    });
  };

  const initializeHeaders = (root) => headersWithin(root).forEach(initializeHeader);
  const disposeHeaders = (root) => headersWithin(root).forEach((header) => headerInstances.get(header)?.());

  const videosWithin = (root) => {
    const videos = [];
    if (root?.matches?.(videoSelector)) videos.push(root);
    root?.querySelectorAll?.(videoSelector).forEach((video) => videos.push(video));
    return videos;
  };

  const getVideoObserver = () => {
    if (!window.IntersectionObserver) return undefined;
    if (videoObserver) return videoObserver;

    videoObserver = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const activation = videoActivations.get(entry.target);
        if (!activation) return;
        if (entry.isIntersecting) activation.enter();
        else activation.leave();
      });
    }, { rootMargin: '400px 200px', threshold: 0.01 });
    return videoObserver;
  };

  const initializeVideo = (card) => {
    if (videoInstances.has(card) || card.dataset.drdentVideoInitialized === 'true') return;

    const video = card.querySelector('video');
    const button = card.querySelector('[data-drdent-video-play]');
    const status = card.querySelector('[data-drdent-video-status]');
    const glyph = button?.querySelector('span');
    if (!video || !button || !status || !glyph) return;

    let disposed = false;
    let activated = false;
    let pendingPlay = false;
    let unavailable = false;

    const activateVideo = () => {
      if (activated || disposed || unavailable) return;
      const source = video.dataset.drdentVideoSrc;
      if (!source) return;
      video.setAttribute('src', source);
      video.load();
      activated = true;
    };

    const setPlayLabel = () => {
      button.setAttribute('aria-label', 'Play video');
      button.dataset.drdentVideoState = 'play';
      glyph.textContent = '▶';
    };
    const setPauseLabel = () => {
      button.setAttribute('aria-label', 'Pause video');
      button.dataset.drdentVideoState = 'pause';
      glyph.textContent = 'Ⅱ';
    };
    const onPlay = () => {
      if (disposed || unavailable) {
        video.pause();
        return;
      }
      setPauseLabel();
    };
    const onPlaying = () => {
      if (!disposed && !unavailable) card.dataset.drdentVideoReady = 'true';
    };
    const onPause = () => setPlayLabel();
    const onEnded = () => setPlayLabel();
    const onError = () => {
      unavailable = true;
      delete card.dataset.drdentVideoReady;
      video.pause();
      setPlayLabel();
      button.dataset.drdentVideoState = 'error';
      glyph.textContent = '!';
      button.setAttribute('aria-disabled', 'true');
      status.textContent = 'Video unavailable';
    };
    const onClick = async () => {
      if (disposed || unavailable || pendingPlay) return;

      if (!video.paused) {
        video.pause();
        return;
      }

      activateVideo();
      if (!activated) return;
      pendingPlay = true;
      try {
        await video.play();
        if (disposed || unavailable) {
          if (!video.paused) video.pause();
          return;
        }
        setPauseLabel();
      } catch (_error) {
        setPlayLabel();
      } finally {
        pendingPlay = false;
      }
    };

    button.removeAttribute('aria-disabled');
    status.textContent = '';
    button.addEventListener('click', onClick);
    video.addEventListener('play', onPlay);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('error', onError);
    if (video.paused) setPlayLabel();
    else {
      setPauseLabel();
      if (video.readyState >= 3) card.dataset.drdentVideoReady = 'true';
    }

    const observer = getVideoObserver();
    const onEnterViewport = () => {
      if (disposed || unavailable) return;
      activateVideo();
      if (!activated || !video.paused || pendingPlay) return;
      pendingPlay = true;
      video.play().catch(() => setPlayLabel()).finally(() => {
        pendingPlay = false;
      });
    };
    const onLeaveViewport = () => {
      if (activated && !video.paused) video.pause();
    };
    videoActivations.set(card, { enter: onEnterViewport, leave: onLeaveViewport });
    if (observer) observer.observe(card);
    else activateVideo();

    card.dataset.drdentVideoInitialized = 'true';
    videoInstances.set(card, () => {
      disposed = true;
      observer?.unobserve(card);
      videoActivations.delete(card);
      button.removeEventListener('click', onClick);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('error', onError);
      video.pause();
      delete card.dataset.drdentVideoReady;
      videoInstances.delete(card);
      delete card.dataset.drdentVideoInitialized;
    });
  };

  const initializeVideos = (root) => videosWithin(root).forEach(initializeVideo);
  const disposeVideos = (root) => videosWithin(root).forEach((video) => videoInstances.get(video)?.());

  const faqsWithin = (root) => {
    const faqs = [];
    if (root?.matches?.(faqSelector)) faqs.push(root);
    root?.querySelectorAll?.(faqSelector).forEach((faq) => faqs.push(faq));
    return faqs;
  };

  const initializeFaq = (faq) => {
    if (faqInstances.has(faq) || faq.dataset.drdentFaqInitialized === 'true') return;

    const summaryListeners = [...faq.querySelectorAll('.drdent-faq__item')].flatMap((details) => {
      const summary = details.querySelector('summary');
      if (!summary) return [];
      const onKeyDown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
        event.preventDefault();
        details.open = !details.open;
      };
      summary.addEventListener('keydown', onKeyDown);
      return [{ listener: onKeyDown, summary }];
    });
    if (!summaryListeners.length) return;

    faq.dataset.drdentFaqInitialized = 'true';
    faqInstances.set(faq, () => {
      summaryListeners.forEach(({ listener, summary }) => summary.removeEventListener('keydown', listener));
      faqInstances.delete(faq);
      delete faq.dataset.drdentFaqInitialized;
    });
  };

  const initializeFaqs = (root) => faqsWithin(root).forEach(initializeFaq);
  const disposeFaqs = (root) => faqsWithin(root).forEach((faq) => faqInstances.get(faq)?.());

  document.addEventListener('DOMContentLoaded', () => {
    initializeAnnouncements(document);
    initializeHeaders(document);
    initializeVideos(document);
    initializeFaqs(document);
  }, { once: true });
  document.addEventListener('shopify:section:load', (event) => {
    initializeAnnouncements(event.target);
    initializeHeaders(event.target);
    initializeVideos(event.target);
    initializeFaqs(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    disposeAnnouncements(event.target);
    disposeHeaders(event.target);
    disposeVideos(event.target);
    disposeFaqs(event.target);
  });
})();
